import { useState, type ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";

import { apiClient } from "../api/client";
import { AdminNav } from "./AdminPage";

/**
 * Cycle 22 STORY-CC3 — Admin Audit Log query UI.
 *
 * Forensics + compliance view для super-admin. Filters по tenant, actor,
 * action, status, time window. Read-only — audit entries не editable
 * (append-only invariant).
 *
 * Backend: GET /api/admin/audit-log с pagination + filters.
 */

type AuditEntry = {
  id: string;
  tenant_id: string;
  actor_email: string | null;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  ip: string | null;
  user_agent: string | null;
  extra_metadata: Record<string, unknown> | null;
  status: string;
  created_at: string;
};

type AuditLogResponse = {
  items: AuditEntry[];
  total: number;
  limit: number;
  offset: number;
};

export function AdminAuditLogPage(): ReactElement {
  const [tenantFilter, setTenantFilter] = useState("");
  const [actorFilter, setActorFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sinceDays, setSinceDays] = useState(7);

  const query = useQuery<AuditLogResponse>({
    queryKey: ["admin-audit-log", tenantFilter, actorFilter, actionFilter, statusFilter, sinceDays],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (tenantFilter) params.append("tenant_id", tenantFilter);
      if (actorFilter) params.append("actor_email", actorFilter);
      if (actionFilter) params.append("action", actionFilter);
      if (statusFilter) params.append("status", statusFilter);
      params.append("since_days", String(sinceDays));
      params.append("limit", "100");
      const r = await apiClient.get(`/api/admin/audit-log?${params.toString()}`);
      return r.data;
    },
  });

  return (
    <div style={{ padding: "20px", fontFamily: "system-ui" }}>
      <AdminNav />
      <h1>📋 Audit Log</h1>
      <p style={{ color: "#666" }}>
        Forensic trail админ-действий: tenant CRUD, login, suspend, etc.
        Append-only (нельзя редактировать). Default window — последние 7 дней.
      </p>

      <section style={filterBoxStyle}>
        <h2 style={{ fontSize: "16px", marginTop: 0 }}>Фильтры</h2>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
          <label>
            Tenant slug
            <input
              type="text"
              value={tenantFilter}
              onChange={(e) => setTenantFilter(e.target.value)}
              placeholder="acme-pilot"
              style={inputStyle}
            />
          </label>
          <label>
            Actor email
            <input
              type="email"
              value={actorFilter}
              onChange={(e) => setActorFilter(e.target.value)}
              placeholder="admin@example.com"
              style={inputStyle}
            />
          </label>
          <label>
            Action (prefix)
            <input
              type="text"
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              placeholder="admin.tenant."
              style={inputStyle}
            />
          </label>
          <label>
            Status
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={inputStyle}
            >
              <option value="">Все</option>
              <option value="success">success</option>
              <option value="error">error</option>
              <option value="denied">denied</option>
            </select>
          </label>
          <label>
            Период (дней)
            <select
              value={sinceDays}
              onChange={(e) => setSinceDays(Number(e.target.value))}
              style={inputStyle}
            >
              <option value="1">Сегодня</option>
              <option value="7">7 дней</option>
              <option value="30">30 дней</option>
              <option value="90">90 дней</option>
              <option value="365">Год</option>
            </select>
          </label>
        </div>
      </section>

      <section style={{ marginTop: "20px" }}>
        {query.isLoading && <p>Загрузка...</p>}
        {query.isError && (
          <p style={{ color: "red" }}>
            Ошибка загрузки audit log: {(query.error as Error).message}
          </p>
        )}

        {query.data && (
          <>
            <p style={{ color: "#666" }}>
              Найдено: <strong>{query.data.total}</strong>
              {query.data.total > query.data.limit && (
                <span> (показаны первые {query.data.limit})</span>
              )}
            </p>
            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "12px" }}>
              <thead>
                <tr style={{ background: "#f5f5f5", textAlign: "left" }}>
                  <th style={th}>Время</th>
                  <th style={th}>Tenant</th>
                  <th style={th}>Actor</th>
                  <th style={th}>Action</th>
                  <th style={th}>Status</th>
                  <th style={th}>Detail</th>
                </tr>
              </thead>
              <tbody>
                {query.data.items.map((entry) => (
                  <tr key={entry.id} style={{ borderBottom: "1px solid #eee" }}>
                    <td style={td}>
                      <time title={entry.created_at}>
                        {new Date(entry.created_at).toLocaleString("ru-RU")}
                      </time>
                    </td>
                    <td style={td}>
                      <code>{entry.tenant_id}</code>
                    </td>
                    <td style={td}>{entry.actor_email || "—"}</td>
                    <td style={td}>
                      <code style={{ fontSize: "12px" }}>{entry.action}</code>
                    </td>
                    <td style={td}>
                      <StatusBadge status={entry.status} />
                    </td>
                    <td style={{ ...td, fontSize: "12px", color: "#666" }}>
                      {entry.resource_type && (
                        <div>
                          {entry.resource_type}: <code>{entry.resource_id}</code>
                        </div>
                      )}
                      {entry.extra_metadata && (
                        <details>
                          <summary style={{ cursor: "pointer" }}>metadata</summary>
                          <pre style={{ fontSize: "11px", background: "#f9f9f9", padding: "4px" }}>
                            {JSON.stringify(entry.extra_metadata, null, 2)}
                          </pre>
                        </details>
                      )}
                      {entry.ip && <div>IP: {entry.ip}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {query.data.items.length === 0 && (
              <p style={{ color: "#888", textAlign: "center", padding: "40px" }}>
                Нет записей по выбранным фильтрам.
              </p>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function StatusBadge({ status }: { status: string }): ReactElement {
  const colors: Record<string, { bg: string; fg: string }> = {
    success: { bg: "#e6f7ee", fg: "#0a7a4a" },
    error: { bg: "#fde8eb", fg: "#a01c2a" },
    denied: { bg: "#fff7d6", fg: "#a07000" },
  };
  const c = colors[status] || { bg: "#eee", fg: "#666" };
  return (
    <span
      style={{
        background: c.bg,
        color: c.fg,
        padding: "2px 8px",
        borderRadius: "10px",
        fontSize: "12px",
        fontWeight: 500,
      }}
    >
      {status}
    </span>
  );
}

const filterBoxStyle: React.CSSProperties = {
  marginTop: "20px",
  padding: "16px",
  border: "1px solid #ddd",
  borderRadius: "8px",
  background: "#fafafa",
};

const inputStyle: React.CSSProperties = {
  display: "block",
  width: "100%",
  marginTop: "4px",
  padding: "6px 8px",
  fontSize: "13px",
  border: "1px solid #ccc",
  borderRadius: "4px",
};

const th: React.CSSProperties = {
  padding: "10px 8px",
  fontWeight: 600,
  fontSize: "13px",
  color: "#666",
};

const td: React.CSSProperties = {
  padding: "10px 8px",
  fontSize: "13px",
  verticalAlign: "top",
};
